import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Toolbar,
  Divider,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  useTheme
} from '@mui/material';
import {
  ViewInAr,
  Inventory,
  Store,
  Settings,
  Fullscreen,
  FullscreenExit,
  Refresh,
  Save,
  Share
} from '@mui/icons-material';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, Grid as ThreeGrid } from '@react-three/drei';
import TopBar from './components/TopBar';
import InventoryPanel from './components/InventoryPanel';
import StoreLayoutPanel from './components/StoreLayoutPanel';
import SettingsPanel from './components/SettingsPanel';
import GenerationPanel from './components/GenerationPanel';
import ViewerCanvas from './components/ViewerCanvas';
import { useStorefront3DStore } from './store/useStorefront3DStore';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ height: '100%' }}>
    {value === index && (
      <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
        {children}
      </Box>
    )}
  </div>
);

const Storefront3D = () => {
  const theme = useTheme();
  const {
    activeTab,
    setActiveTab,
    isFullscreen,
    setIsFullscreen,
    selectedItems,
    storeLayout,
    viewerSettings
  } = useStorefront3DStore();

  const containerRef = useRef(null);
  const [rightPanelWidth, setRightPanelWidth] = useState(320);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    console.log('Saving 3D storefront...');
  };

  const handleShare = () => {
    // TODO: Implement share functionality
    console.log('Sharing 3D storefront...');
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.default,
        position: 'relative',
        margin: 0,
        padding: 0
      }}
    >
      {/* Top Bar */}
      <TopBar
        onSave={handleSave}
        onShare={handleShare}
        onToggleFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
      />

      <Divider />

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* 3D Viewer Canvas */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            bgcolor: theme.palette.grey[100],
            minWidth: 0
          }}
        >
          <ViewerCanvas />
        </Box>

        {/* Right Panel */}
        {!isFullscreen && (
          <Paper
            sx={{
              width: rightPanelWidth,
              minWidth: 280,
              maxWidth: 500,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              borderLeft: `1px solid ${theme.palette.divider}`
            }}
          >
            {/* Panel Tabs */}
            <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab
                  icon={<Inventory />}
                  label="Inventory"
                  value="inventory"
                  sx={{ minHeight: 48 }}
                />
                <Tab
                  icon={<Store />}
                  label="Layout"
                  value="layout"
                  sx={{ minHeight: 48 }}
                />
                <Tab
                  icon={<Settings />}
                  label="Settings"
                  value="settings"
                  sx={{ minHeight: 48 }}
                />
                <Tab
                  icon={<ViewInAr />}
                  label="3D Generation"
                  value="generation"
                  sx={{ minHeight: 48 }}
                />
              </Tabs>
            </Box>

            {/* Panel Content */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <TabPanel value={activeTab} index="inventory">
                <InventoryPanel />
              </TabPanel>
              <TabPanel value={activeTab} index="layout">
                <StoreLayoutPanel />
              </TabPanel>
              <TabPanel value={activeTab} index="settings">
                <SettingsPanel />
              </TabPanel>
              <TabPanel value={activeTab} index="generation">
                <GenerationPanel />
              </TabPanel>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default Storefront3D;
